import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import EmptyState from "@/components/base/EmptyState";
import FilterSidebar from "@/components/base/FilterSidebar";
import Pagination from "@/components/base/Pagination";
import VideoCard from "@/components/base/VideoCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { defaultFilters, sortOptions, type SortKey, type VideoFilters } from "@/types";
import { applyFilters, countActiveFilters, getCategoryById, getVideosByCategory, sortVideos } from "@/utils/videoData";

const PAGE_SIZE = 8;

export default function ListPage() {
  const { categoryId } = useParams();
  const category = getCategoryById(categoryId);
  const [filters, setFilters] = useState<VideoFilters>(defaultFilters);
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setFilters(defaultFilters);
    setSort("latest");
    setPage(1);
  }, [categoryId]);

  useEffect(() => {
    setPage(1);
  }, [filters, sort]);

  const results = useMemo(() => {
    const base = category ? getVideosByCategory(category.id) : [];
    return sortVideos(applyFilters(base, filters), sort);
  }, [category, filters, sort]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const pageItems = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  usePageMeta({
    title: category ? `${category.name}视频库 - 一起草视频` : "视频库 - 一起草视频",
    description: category
      ? `在“${category.name}”视频库中按地区、类型、年份、时长多维筛选，快速找到想看的视频。`
      : "一起草视频视频库，支持多维筛选与排序。",
  });

  if (!category) {
    return (
      <div className="px-4 md:px-6 py-16 max-w-3xl mx-auto">
        <EmptyState
          icon="ri-error-warning-line"
          title="该分类不存在"
          description="你访问的分类可能已被移除，返回首页看看其他精彩内容吧。"
          actionText="返回首页"
          actionLink="/"
        />
      </div>
    );
  }

  const activeCount = countActiveFilters(filters);

  return (
    <div className="bg-background-50">
      <div className="px-4 md:px-6 pt-6 md:pt-8">
        <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs md:text-sm text-foreground-500 mb-5">
          <Link to="/" className="hover:text-primary-600 transition-colors">
            首页
          </Link>
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-s-line text-base" />
          </span>
          <Link to={`/category/${category.id}`} className="hover:text-primary-600 transition-colors">
            {category.name}
          </Link>
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-s-line text-base" />
          </span>
          <span className="text-foreground-900 whitespace-nowrap">视频库</span>
        </nav>
        <h1 className="text-2xl md:text-3xl font-extrabold text-foreground-950">
          {category.name}<span className="text-primary-600">视频库</span>
        </h1>
        <p className="mt-2 text-sm md:text-base text-foreground-500 max-w-2xl">{category.description}</p>
      </div>

      <div className="px-4 md:px-6 py-6 md:py-8">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-24 p-5 rounded-lg bg-background-100 border border-background-200">
              <FilterSidebar filters={filters} onChange={setFilters} />
            </div>
          </aside>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-3 p-3 md:p-4 rounded-lg bg-background-100 border border-background-200 mb-5">
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="lg:hidden inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-filter-3-line text-base" />
                </span>
                筛选{activeCount > 0 ? ` (${activeCount})` : ""}
              </button>

              <span className="text-sm text-foreground-500 hidden sm:inline">排序</span>
              <div className="flex items-center gap-1 p-1 rounded-full bg-background-200/70">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setSort(option.key)}
                    className={`px-3.5 py-1.5 rounded-full text-xs md:text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      sort === option.key
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <p className="ml-auto text-sm text-foreground-500 whitespace-nowrap">
                共 <span className="font-semibold text-foreground-900">{results.length}</span> 个结果
              </p>
            </div>

            {pageItems.length === 0 ? (
              <EmptyState
                icon="ri-filter-off-line"
                title="没有符合条件的视频"
                description="试着放宽筛选条件，或重置后再看看。"
              />
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
                  {pageItems.map((video, idx) => (
                    <VideoCard key={video.id} video={video} index={idx} />
                  ))}
                </div>
                <Pagination current={page} total={totalPages} onChange={setPage} />
              </>
            )}
          </div>
        </div>
      </div>

      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]">
          <div
            className="absolute inset-0 bg-foreground-950/50 animate-fade-in"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto bg-background-50 rounded-t-2xl p-5 animate-fade-up">
            <div className="flex items-center justify-between mb-2">
              <span className="text-base font-bold text-foreground-950">筛选条件</span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="关闭"
                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <FilterSidebar filters={filters} onChange={setFilters} />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-6 w-full h-11 rounded-md bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              查看 {results.length} 个结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}